import {
  parsePhoneNumberWithError,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { COUNTRY_CODE_LIST } from "../config/constants";

/**
 * Detect the user's default country from browser locale.
 * e.g. "en-US" -> "US", "fr-CA" -> "CA"
 * Falls back to "US" if detection fails or country is not in our list.
 */
export const getDefaultCountry = () => {
  try {
    const locale = navigator.language || navigator.userLanguage || "";
    // Extract country from locale like "en-US", "fr-CA", "pt-BR"
    const parts = locale.split("-");
    if (parts.length >= 2) {
      const country = parts[parts.length - 1].toUpperCase();
      if (COUNTRY_CODE_LIST.some((c) => c.id === country)) {
        return country;
      }
    }
  } catch {
    // navigator may not be available in SSR/tests
  }
  return "US";
};

/**
 * Attempt to detect the country from a phone number input.
 * Returns the country code (e.g. "US", "GB") or undefined if detection fails.
 */
export const detectCountryFromInput = (value) => {
  if (!value || !value.trim()) return undefined;
  try {
    const parsed = parsePhoneNumberFromString(value);
    return parsed?.country || undefined;
  } catch {
    return undefined;
  }
};

export const validatePhoneNumber = (phoneNumber, setError, defaultCountry) => {
  // Allow empty phone numbers (optional field)
  if (!phoneNumber || phoneNumber.trim() === "") {
    return true;
  }

  try {
    const parsedPhoneNumber = parsePhoneNumberWithError(
      phoneNumber,
      defaultCountry
    );
    const isValid = parsedPhoneNumber.isValid();
    if (!isValid) {
      setError("phoneNumber", {
        type: "manual",
        message: "phone number is invalid",
      });
      return false;
    }
  } catch (error) {
    setError("phoneNumber", {
      type: "manual",
      message: error.message,
    });
    return false;
  }
  return true;
};

export const validatePhoneNumberWithCustomError = (
  phoneNumber,
  setErrors,
  defaultCountry
) => {
  // Allow empty phone numbers (optional field)
  if (!phoneNumber || phoneNumber.trim() === "") {
    return true;
  }

  try {
    const parsedPhoneNumber = parsePhoneNumberWithError(
      phoneNumber,
      defaultCountry
    );
    const isValid = parsedPhoneNumber.isValid();
    if (!isValid) {
      setErrors({
        title: "Invalid Phone Number",
        message: "Please enter a valid phone number",
      });
      return false;
    }
  } catch (error) {
    setErrors({
      title: "Invalid Phone Number",
      message: error.message,
    });
    return false;
  }
  return true;
};

export const getInitialCountry = (phoneNumber) => {
  if (phoneNumber) {
    try {
      const parsedPhoneNumber = parsePhoneNumberWithError(phoneNumber);
      return parsedPhoneNumber.country;
    } catch (error) {
      return "";
    }
  }
  return "";
};

/**
 * Format a phone number to E.164 for storage.
 * Uses defaultCountry as a hint for national numbers.
 * Returns the original value if parsing fails.
 */
export const formatToE164 = (value, defaultCountry) => {
  if (!value || !value.trim()) return value;
  try {
    const parsed = parsePhoneNumberFromString(value, defaultCountry);
    if (parsed && parsed.isValid()) {
      return parsed.format("E.164");
    }
  } catch {
    // fall through
  }
  return value;
};
